import React, { createContext, useContext, useState, useRef, ReactNode, useCallback, useEffect } from "react";
import { BaseAddon } from "../base/BaseAddon";
import { StepChangeEvent, StepState, StepType } from "../../types/types";
import { stepIndexToStepType } from "../../utils/steps";

interface AddonsManagerContextType {
  addons: BaseAddon[];
  registerAddon: (addon: BaseAddon) => void;
  clearAddons: () => void;
  getAddonsForStep: (step: StepType) => BaseAddon[];
  currentStep: StepType;
  setCurrentStep: (step: StepType) => void;
  currentStepData: any;
  setCurrentStepData: (data: any) => void;
  onStepChangeHook: (event: StepChangeEvent) => void;
  handleStepChange: (event: StepChangeEvent) => void;
  uploadedFile: File | null;
  setUploadedFile: (file: File | null) => void;
  history: StepChangeEvent[];
  goBack: () => void;
  reset: () => void;
  onStepSelect: (e: number) => void;
}

const AddonsManagerContext = createContext<AddonsManagerContextType | undefined>(undefined);

interface AddonsManagerProviderProps {
  children: ReactNode;
  initialStep?: StepType;
  initialStepData?: any;
  onStepChangeHook?: (event: StepChangeEvent) => void;
  onStepSelectHook?: (stepIdx: number) => void;
  onSubmit?: (data: any, file: File) => void | Promise<any>;
}

export const AddonsManagerProvider: React.FC<AddonsManagerProviderProps> = ({
  children,
  initialStep = StepType.upload,
  initialStepData = { type: StepType.upload },
  onStepChangeHook,
  onStepSelectHook,
  onSubmit
}) => {
  // using ref to prevent rerenders
  const addonsRef = useRef<BaseAddon[]>([]);

  const [currentStep, setCurrentStep] = useState<StepType>();
  const [currentStepData, setCurrentStepData] = useState<StepState>();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [history, setHistory] = useState<StepChangeEvent[]>([]);

  useEffect(() => {
    setCurrentStep(initialStep);
    setCurrentStepData(initialStepData);
    setHistory([...history, { ...initialStepData }]);
    // eslint-disable-next-line
  }, []);

  const registerAddon = useCallback((addon: BaseAddon) => {
    const existingAddonIndex = addonsRef.current.findIndex(a => a.name === addon.name);

    if (existingAddonIndex === -1) {
      addonsRef.current.push(addon);
    } else {
      addonsRef.current[existingAddonIndex] = addon;
    }
  }, []);

  const clearAddons = useCallback(() => {
    addonsRef.current = [];
  }, []);

  const getAddonsForStep = useCallback((step: StepType) => {
    return addonsRef.current.filter(addon => addon.applicableSteps.includes(step));
  }, []);

  const handleStepChange = useCallback(
    (event: StepChangeEvent) => {
      setHistory(prev => {
        setCurrentStep(event.type);
        const newData: StepState = {} as any;

        Object.keys(event).forEach(key => {
          if (key !== "type") {
            newData[key] = event[key];
          }
        });

        // Preserve data from previous steps that might be needed
        if (event.type === StepType.selectHeader) {
          newData.headerValues = currentStepData?.headerValues || event?.headerValues;
        } else if (event.type === StepType.matchColumns) {
          newData.headerValues = currentStepData?.headerValues || event?.headerValues;
        } else if (event.type === StepType.entityRelationValidation) {
        } else if (event.type === StepType.validateData) {
          newData.entityRelations = event?.entityRelations || currentStepData?.entityRelations;
        }

        if (event.file) {
          setUploadedFile(event.file);
        }

        setCurrentStepData(newData);

        if (event.type === StepType.validateData && onSubmit && event.data && uploadedFile) {
          onSubmit(event.data, uploadedFile);
        }

        if (onStepChangeHook) {
          onStepChangeHook(event);
        }

        return [
          ...prev,
          {
            type: event.type,
            ...newData
          }
        ];
      });
    },
    [currentStepData, uploadedFile, onSubmit, onStepChangeHook]
  );

  const goBack = useCallback(() => {
    setHistory(prev => {
      if (!prev || prev.length <= 1) {
        setCurrentStep(StepType.upload);
        return prev;
      }

      const previousState = prev[prev.length - 2];

      if (previousState) {
        setCurrentStep(previousState.type);

        const restoredData = { ...previousState };
        delete restoredData.type;

        setCurrentStepData(restoredData);

        if (onStepChangeHook) {
          onStepChangeHook(previousState);
        }
        return prev.slice(0, -1);
      }
    });

    // eslint-disable-next-line
  }, [onStepChangeHook, setCurrentStep, currentStep, history]);

  const onStepSelect = useCallback(
    (stepIdx: number) => {
      const stepType = stepIndexToStepType(stepIdx);

      setHistory(prev => {
        const selectedIndex = prev?.findIndex(e => e.type === stepType);
        const currentIndex = prev?.findIndex(e => e.type === currentStep);

        if (selectedIndex > -1 && selectedIndex <= currentIndex) {
          const selectedState = prev[selectedIndex];

          setCurrentStep(stepType);

          const restoredData = { ...selectedState };
          delete restoredData.type;
          setCurrentStepData(restoredData);

          if (onStepSelectHook) {
            onStepSelectHook(stepIdx);
          }

          return prev.slice(0, selectedIndex + 1);
        } else {
          console.error(`Cannot navigate to step ${stepType} - not yet completed`);
          return prev;
        }
      });
    },
    [onStepSelectHook, currentStep]
  );
  const reset = useCallback(() => {
    setCurrentStep(initialStep);
    setCurrentStepData(initialStepData);
    setUploadedFile(null);
    setHistory([]);
  }, [initialStep, initialStepData]);

  const value = {
    addons: addonsRef.current,
    registerAddon,
    clearAddons,
    getAddonsForStep,
    currentStep,
    setCurrentStep,
    currentStepData,
    setCurrentStepData,
    onStepChangeHook,
    handleStepChange,
    uploadedFile,
    setUploadedFile,
    history,
    goBack,
    reset,
    onStepSelect
  };

  return <AddonsManagerContext.Provider value={value}>{children}</AddonsManagerContext.Provider>;
};

export const useAddonsManager = () => {
  const context = useContext(AddonsManagerContext);

  if (context === undefined) {
    throw new Error("useAddonsManager must be used within an AddonsManagerProvider");
  }

  return context;
};
